import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './BusinessComponent.css';
import './BusinessFormsPolish.css';
import BusinessCalendarComponent from "./components/BusinessCalendarComponent";
import BusinessPaymentComponent from "./components/BusinessPaymentComponent";
import BusinessNotificationComponent from "./components/BusinessNotificationComponent";
import BusinessDetailsComponent from "./components/BusinessDetailsComponent";
import BusinessPublicProfileComponent from "./components/BusinessPublicProfileComponent";
import { usePermissions } from "../../../../context/PermissionsContext";
import { isSettingsDirty, setSettingsDirty } from "../../../../util/settingsDirty";
import { useConfirm } from "../../../../context/confirm/ConfirmContext";

const BusinessComponent: React.FC = () => {
    const { t } = useTranslation();
    const tr = t; // alias: the tabs map shadows `t` with the tab item
    const confirmModal = useConfirm();
    const { canView } = usePermissions();

    // Sub-tabs gated by permission: the Calendar tab is the BUSINESS CALENDAR
    // (WORKING_HOURS); everything else is company details (BUSINESS_SETTINGS).
    const tabs = useMemo(
        () => [
            { key: 'details', label: t('settings.business.tabs.details'), show: canView('BUSINESS_SETTINGS'), render: () => <BusinessDetailsComponent /> },
            { key: 'publicProfile', label: t('settings.business.tabs.publicProfile'), show: canView('BUSINESS_SETTINGS'), render: () => <BusinessPublicProfileComponent /> },
            { key: 'calendar', label: t('settings.business.tabs.calendar'), show: canView('WORKING_HOURS'), render: () => <BusinessCalendarComponent /> },
            { key: 'payment', label: t('settings.business.tabs.payment'), show: canView('PAYMENT_SETTINGS'), render: () => <BusinessPaymentComponent /> },
            { key: 'notification', label: t('settings.business.tabs.notification'), show: canView('BUSINESS_SETTINGS'), render: () => <BusinessNotificationComponent /> },
        ].filter((t) => t.show),
        [canView, t]
    );

    const [selectedBusinessSideTab, setSelectedBusinessSideTab] = useState<string>('details');

    useEffect(() => {
        if (tabs.length === 0) return;
        if (!tabs.some((t) => t.key === selectedBusinessSideTab)) {
            setSelectedBusinessSideTab(tabs[0].key);
        }
    }, [tabs, selectedBusinessSideTab]);

    const active = tabs.find((t) => t.key === selectedBusinessSideTab) ?? tabs[0];

    return (
        <div className="profile-content">
            <div className="side-menu">
                {tabs.map((t) => (
                    <button
                        key={t.key}
                        data-cy={`biz-tab-${t.key}`}
                        className={`lateral-button ${selectedBusinessSideTab === t.key ? 'active' : ''}`}
                        onClick={async () => {
                            // QA decision 2026-07-04: never lose typed data silently —
                            // confirm before leaving a section with unsaved changes
                            // (ConfirmModal do app, nunca window.confirm).
                            if (t.key !== selectedBusinessSideTab && isSettingsDirty()) {
                                const ok = await confirmModal({
                                    title: tr('common.confirmTitle'),
                                    message: tr('settings.unsavedTabConfirm'),
                                    danger: true,
                                    confirmLabel: tr('common.discard'),
                                });
                                if (!ok) return;
                            }
                            setSettingsDirty(false);
                            setSelectedBusinessSideTab(t.key);
                        }}
                    >
                        {t.label}
                    </button>
                ))}
            </div>
            <div className="side-content-area">
                {active ? active.render() : null}
            </div>
        </div>
    );
};

export default BusinessComponent;
